import { Link, useSearchParams } from 'react-router-dom'
import { gql } from 'graphql-request'

import { useQuery } from '@hooks/graphql'
import { durationInWords } from '@helpers/format'
import TutorCard from '@/educators/components/TutorCard'
import Pagination from '@components/Pagination'
import Spinner from '@components/Spinner'
import SegmentedControl from '@components/SegmentedControl'

import NoResults from './NoResults'

const CLASSROOM_TUTORS_QUERY = gql`
  query classroomTutors($id: ID!, $page: Int, $closed: Boolean) {
    classroom(id: $id) {
      tutorAssignments(closed: $closed, page: $page, perPage: 6) {
        nodes {
          id
          createdAt
          closed
          closedAt
          tutor {
            id
            name
            summary
            description
            image {
              url
            }
            userName
            socratic
            stemModeEnabled
            copiesCount
          }
          educator {
            firstName
            lastName
            fullName
          }
        }
        pagesCount
        nodesCount
      }
    }
  }
`

const OpenTutorList = ({ id }) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const page = searchParams.get('page') || 1
  const status = searchParams.get('status') || 'open'
  const { isLoading, data: { classroom: { tutorAssignments: { nodes: assignments = [], pagesCount, nodesCount } = {} } = {} } = {} } = useQuery({
    queryKey: ['classroomTutors', id, page, status],
    gqlQuery: CLASSROOM_TUTORS_QUERY,
    variables: { id, page: parseInt(page), closed: status ? status === 'closed' : null }
  })

  const setPage = page => {
    if (status === null) {
      setSearchParams({ page })
    } else {
      setSearchParams({ page, status })
    }
  }

  return (
    <div className='px-5 pt-4'>
      <SegmentedControl
        className='mb-4'
        options={[
          { label: 'Active', value: 'open' },
          { label: 'Closed', value: 'closed' }
        ]}
        value={status}
        onChange={value => {
          setSearchParams({ status: value })
        }}
      />

      <Choose>
        <When condition={isLoading}>
          <Spinner className='flex items-center justify-center w-full h-full' />
        </When>

        <When condition={nodesCount === 0}>
          <NoResults />
        </When>

        <Otherwise>
          <div className='grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-2 md:gap-4'>
            <For each='assignment' of={assignments}>
              <With tutor={assignment.tutor}>
                <Link
                  key={assignment.id}
                  to={`/educators/classrooms/${id}/tutors/${assignment.tutor.id}/sessions/${assignment.id}/${assignment.closed ? 'insights' : ''}`}
                >
                  <TutorCard
                    tutorId={tutor.id}
                    name={tutor.name}
                    description={tutor.summary || tutor.description}
                    imageUrl={tutor.image?.url}
                    userName={tutor.userName}
                    socratic={tutor.socratic}
                    stemModeEnabled={tutor.stemModeEnabled}
                    type='classroom'
                    assignerName={assignment.educator.fullName}
                    assignmentState={assignment.closed ? 'closed' : 'open'}
                    assignmentStateDuration={assignment.closed ? durationInWords(assignment.closedAt) : durationInWords(assignment.createdAt)}
                  />
                </Link>
              </With>
            </For>
          </div>

          <Pagination page={parseInt(page)} pagesCount={pagesCount} setPage={setPage} />
        </Otherwise>
      </Choose>
    </div>
  )
}

export default OpenTutorList
